import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaCheckCircle,
  FaTint,
  FaShieldAlt,
  FaThermometerHalf,
  FaHeart,
  FaLeaf,
  FaCertificate,
  FaBarcode,
  FaGlassWhiskey,
  FaTemperatureHigh,
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaTimes,
} from "react-icons/fa";

const KingCoconutWater = () => {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const productKeys = [
    "natural",
    "passionFruit",
    "pineapple",
    "cinnamon",
    "ginger",
  ];

  const productImages = {
    natural: [
      "/images/products/natural-king-coconut-water-bottle.webp",
      "/images/products/natural-king-coconut-water-pouring.webp",
    ],
    passionFruit: [
      "/images/products/passion-fruit-king-coconut-water-bottle.webp",
      "/images/products/passion-fruit-king-coconut-water-splash.webp",
    ],
    pineapple: [
      "/images/products/pineapple-king-coconut-water-bottle.webp",
      "/images/products/pineapple-king-coconut-water-glass.webp",
    ],
    cinnamon: [
      "/images/products/cinnamon-king-coconut-water-bottle.webp",
      "/images/products/cinnamon-king-coconut-water-ingredients.webp",
    ],
    ginger: [
      "/images/products/ginger-king-coconut-water-bottle.webp",
      "/images/products/ginger-king-coconut-water-fresh.webp",
    ],
  };

  const productDataSheets = {
    natural: "/images/DataSheet/natural-king-coconut-water.jpg",
    passionFruit: "/images/DataSheet/passion-fruit-king-coconut-water.jpg",
    pineapple: "/images/DataSheet/pineapple-king-coconut-water.jpg",
    cinnamon: "/images/DataSheet/cinnamon-king-coconut-water.jpg",
    ginger: "/images/DataSheet/ginger-king-coconut-water.jpg",
  };

  const productEmojis = {
    natural: "🥥",
    passionFruit: "🥥🌺",
    pineapple: "🥥🍍",
    cinnamon: "🥥🌿",
    ginger: "🥥🫚",
  };

  const products = productKeys.map((key) => ({
    key,
    name: t(`kingCoconutWater.products.${key}.name`),
    images: productImages[key] || [
      "/images/products/default-king-coconut-water-1.webp",
      "/images/products/default-king-coconut-water-2.webp",
    ],
    image: productEmojis[key] || "🥥",
    dataSheet: productDataSheets[key],
    color:
      key === "natural"
        ? "from-orange-400 to-amber-600"
        : key === "passionFruit"
        ? "from-purple-400 to-pink-600"
        : key === "pineapple"
        ? "from-yellow-400 to-orange-600"
        : key === "cinnamon"
        ? "from-amber-600 to-brown-700"
        : "from-yellow-600 to-orange-700",
  }));

  
  const certifications = [
    {
      name: t("dehydratedFruits.certifications.brc.name"),
      image: "/images/certifications/BRC.webp",
      alt: t("dehydratedFruits.certifications.brc.name"),
      description: t("dehydratedFruits.certifications.brc.description"),
      icon: "🏆",
    },
    {
      name: t("dehydratedFruits.certifications.eu.name"),
      image: "/images/certifications/EU.webp",
      alt: t("dehydratedFruits.certifications.eu.name"),
      description: t("dehydratedFruits.certifications.eu.description"),
      icon: "🇪🇺",
    },
    {
      name: t("dehydratedFruits.certifications.usda.name"),
      image: "/images/certifications/USDA.webp",
      alt: t("dehydratedFruits.certifications.usda.name"),
      description: t("dehydratedFruits.certifications.usda.description"),
      icon: "🇺🇸",
    },
    {
      name: t("dehydratedFruits.certifications.jas.name"),
      image: "/images/certifications/JAS.webp",
      alt: t("dehydratedFruits.certifications.jas.name"),
      description: t("dehydratedFruits.certifications.jas.description"),
      icon: "🇯🇵",
    },
    {
      name: t("dehydratedFruits.certifications.naturland.name"),
      image: "/images/certifications/Naturland.webp",
      alt: t("dehydratedFruits.certifications.naturland.name"),
      description: t("dehydratedFruits.certifications.naturland.description"),
      icon: "🌱",
    },
    {
      name: t("dehydratedFruits.certifications.fairtrade.name"),
      image: "/images/certifications/FairTrade.webp",
      alt: t("dehydratedFruits.certifications.fairtrade.name"),
      description: t("dehydratedFruits.certifications.fairtrade.description"),
      icon: "🤝",
    },
    {
      name: t("dehydratedFruits.certifications.fda.name"),
      image: "/images/certifications/FDA.webp",
      alt: t("dehydratedFruits.certifications.fda.name"),
      description: t("dehydratedFruits.certifications.fda.description"),
      icon: "✅",
    },
  ];

  const faqKeys = ["sugar", "organic", "privateLabel", "shelfLife"];

  // Reset image index when product changes
  const handleProductChange = (index) => {
    setActiveProduct(index);
    setActiveImageIndex(0);
  };

  // Navigate images
  const nextImage = () => {
    setActiveImageIndex((prev) =>
      prev === products[activeProduct].images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setActiveImageIndex((prev) =>
      prev === 0 ? products[activeProduct].images.length - 1 : prev - 1
    );
  };

  // Get translated product data
  const getProductData = (productKey, field) => {
    const path = `kingCoconutWater.products.${productKey}.${field}`;
    const value = t(path, { returnObjects: true });
    return value === path ? null : value;
  };

  // Download modal component
  const DownloadModal = () => (
    <AnimatePresence>
      {showDownloadModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDownloadModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" // ✅ Changed: Added flex flex-col and overflow-hidden
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Fixed at top */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              {" "}
              {/* ✅ Added: flex-shrink-0 */}
              <div>
                <h3 className="text-2xl font-bold mb-1">
                  {t("dehydratedFruits.downloadModal.title")}
                </h3>
                <p className="text-amber-100 text-sm">
                  {t("dehydratedFruits.downloadModal.subtitle")}
                </p>
              </div>
              <button
                onClick={() => setShowDownloadModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-300"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Product List - Scrollable area */}
            <div className="p-6 space-y-3 overflow-y-auto flex-1">
              {" "}
              {/* ✅ Changed: Added flex-1 for proper scrolling */}
              {products.map((product, index) => (
                <motion.a
                  key={index}
                  href={product.dataSheet}
                  download
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-gray-700 dark:to-gray-600 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-4">
                    {/* Product Icon */}
                    <div
                      className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center text-3xl shadow-md`}
                    >
                      {product.image}
                    </div>

                    {/* Product Info */}
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                        {product.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t("dehydratedFruits.downloadModal.productInfo")}
                      </p>
                    </div>
                  </div>

                  {/* Download Icon */}
                  <div className="bg-amber-600 text-white rounded-full p-3 group-hover:bg-amber-700 group-hover:scale-110 transition-all duration-300 shadow-md">
                    <FaDownload className="text-xl" />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* SEO Component */}
      <SEO
        title="Organic King Coconut Water | Premium Ceylon Coconut Water"
        description="Pure organic King Coconut Water from Sri Lanka. BRC certified, naturally hydrating with no added sugars. Available in Natural, Passion Fruit, Pineapple, Cinnamon & Ginger flavors. 18-month shelf life."
        keywords="organic king coconut water, Ceylon coconut water, natural coconut water, organic coconut water Sri Lanka, BRC certified coconut water, king coconut water benefits, passion fruit coconut water, pineapple coconut water, cinnamon coconut water, ginger coconut water, premium coconut water, organic hydration drink, natural electrolytes, USDA organic coconut water, EU organic coconut water, Fairtrade coconut water, traceable coconut water, sustainable coconut water"
        image="https://worganaturals.com/images/products/natural-king-coconut-water-bottle.webp"
        url="/products/king-coconut-water"
        type="product"
        productSchema={true}
        category="Organic Beverages"
        brand="Worga Naturals"
        availability="InStock"
        price="2.99"
        currency="USD"
        sku="KCW-NAT-200"
      />

      {/* Additional Structured Data */}
      <Helmet>
        {/* Product Schema with Variants */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Organic King Coconut Water",
            description:
              "Pure organic King Coconut Water from Sri Lanka. BRC certified, naturally hydrating with no added sugars. Available in multiple flavors.",
            brand: {
              "@type": "Brand",
              name: "Worga Naturals",
            },
            manufacturer: {
              "@type": "Organization",
              name: "Worga Naturals (Pvt) Ltd.",
              address: {
                "@type": "PostalAddress",
                streetAddress: "No. 152/G, Meepavita, Udamapitigama",
                addressLocality: "Dompe",
                postalCode: "11680",
                addressCountry: "LK",
              },
            },
            image: [
              "https://worganaturals.com/images/products/natural-king-coconut-water-bottle.webp",
              "https://worganaturals.com/images/products/passion-fruit-king-coconut-water-bottle.webp",
              "https://worganaturals.com/images/products/pineapple-king-coconut-water-bottle.webp",
              "https://worganaturals.com/images/products/cinnamon-king-coconut-water-bottle.webp",
              "https://worganaturals.com/images/products/ginger-king-coconut-water-bottle.webp",
            ],
            category: "Organic Beverages",
            offers: {
              "@type": "AggregateOffer",
              availability: "https://schema.org/InStock",
              priceCurrency: "USD",
              lowPrice: "2.99",
              highPrice: "4.99",
              offerCount: "5",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.8",
              reviewCount: "127",
            },
            additionalProperty: [
              {
                "@type": "PropertyValue",
                name: "Shelf Life",
                value: "18 months",
              },
              {
                "@type": "PropertyValue",
                name: "Pack Sizes",
                value: "200ml, 350ml",
              },
              {
                "@type": "PropertyValue",
                name: "Certifications",
                value:
                  "BRC, EU Organic, USDA Organic, JAS, Naturland, Fairtrade",
              },
              {
                "@type": "PropertyValue",
                name: "Origin",
                value: "Sri Lanka",
              },
            ],
            hasVariant: [
              {
                "@type": "Product",
                name: "Natural King Coconut Water",
                description:
                  "100% organic King coconut water with no added sugars",
                sku: "KCW-NAT-200",
                offers: {
                  "@type": "Offer",
                  price: "2.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "King Coconut Water with Passion Fruit",
                description:
                  "Organic King coconut water blended with passion fruit",
                sku: "KCW-PAS-200",
                offers: {
                  "@type": "Offer",
                  price: "3.49",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "King Coconut Water with Pineapple",
                description: "Organic King coconut water with pineapple",
                sku: "KCW-PIN-200",
                offers: {
                  "@type": "Offer",
                  price: "3.49",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "King Coconut Water with Cinnamon",
                description:
                  "Organic King coconut water infused with Ceylon cinnamon",
                sku: "KCW-CIN-200",
                offers: {
                  "@type": "Offer",
                  price: "3.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "King Coconut Water with Ginger",
                description: "Organic King coconut water with fresh ginger",
                sku: "KCW-GIN-200",
                offers: {
                  "@type": "Offer",
                  price: "3.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
            ],
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is sugar added to Worga Naturals King Coconut Water?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No—no added sugars, colors, flavors, or preservatives. Our King Coconut Water is 100% natural and organic.",
                },
              },
              {
                "@type": "Question",
                name: "Is Worga Naturals King Coconut Water truly organic?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—certified EU, USDA (NOP), JAS, and Naturland; traceable by batch. Each bottle carries a unique batch code to verify origin and certifications.",
                },
              },
              {
                "@type": "Question",
                name: "Can I get private label King Coconut Water?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—custom labels and pack sizes available. Contact us for private label and bulk order inquiries.",
                },
              },
              {
                "@type": "Question",
                name: "What is the shelf life of King Coconut Water?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "18 months from date of manufacture in unopened bottles. Refrigerate after opening and consume within 2-3 days.",
                },
              },
            ],
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Products",
                item: "https://worganaturals.com/products",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "King Coconut Water",
                item: "https://worganaturals.com/products/king-coconut-water",
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Download Modal */}
        <DownloadModal />

        {/* Spacer for navbar */}
        <div className="h-24"></div>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-6"
            >
              <span className="px-6 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-full text-sm font-semibold shadow-lg">
                {t("kingCoconutWater.hero.badge")}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-gray-900 dark:text-white">
                {t("kingCoconutWater.hero.title.part1")}{" "}
              </span>
              <span className="text-orange-600 dark:text-orange-500">
                {t("kingCoconutWater.hero.title.part2")}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto mb-8">
              {t("kingCoconutWater.hero.description")}
            </p>

            {/* Traceability Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-full px-6 py-3 shadow-lg"
            >
              <FaBarcode className="text-orange-600 text-2xl" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {t("kingCoconutWater.hero.traceability")}
              </span>
            </motion.div>
          </motion.div>
        </section>

        {/* Product Line Cards */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("kingCoconutWater.productLine.title")}
            </h2>

            {/* Product Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {products.map((product, index) => (
                <button
                  key={index}
                  onClick={() => handleProductChange(index)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeProduct === index
                      ? "bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-lg scale-105"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md"
                  }`}
                >
                  {product.name}
                </button>
              ))}
            </div>

            {/* Active Product Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProduct}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden"
              >
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Product Image Slider */}
                  <div
                    className={`bg-gradient-to-br ${products[activeProduct].color} flex items-center justify-center p-12 md:p-16 relative overflow-hidden`}
                  >
                    {/* Image Display */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeImageIndex}
                        initial={{ opacity: 0, scale: 0.9, x: 100 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.9, x: -100 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="w-full h-full flex items-center justify-center"
                      >
                        <img
                          src={products[activeProduct].images[activeImageIndex]}
                          alt={products[activeProduct].name}
                          className="max-w-full max-h-[500px] object-contain drop-shadow-2xl rounded-lg"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML = `<div class="text-9xl">🥥</div>`;
                          }}
                        />
                      </motion.div>
                    </AnimatePresence>

                    {/* Navigation Arrows */}
                    {products[activeProduct].images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                          aria-label={t(
                            "kingCoconutWater.navigation.previousImage"
                          )}
                        >
                          <FaChevronLeft className="text-orange-600 text-2xl" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                          aria-label={t(
                            "kingCoconutWater.navigation.nextImage"
                          )}
                        >
                          <FaChevronRight className="text-orange-600 text-2xl" />
                        </button>
                      </>
                    )}

                    {/* Image Indicators */}
                    {products[activeProduct].images.length > 1 && (
                      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 bg-black/30 backdrop-blur-md rounded-full px-4 py-2">
                        {products[activeProduct].images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setActiveImageIndex(index)}
                            className={`transition-all duration-300 rounded-full ${
                              activeImageIndex === index
                                ? "bg-white w-10 h-3"
                                : "bg-white/50 hover:bg-white/75 w-3 h-3"
                            }`}
                            aria-label={`${t(
                              "kingCoconutWater.navigation.goToImage"
                            )} ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Image Counter */}
                    <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {activeImageIndex + 1}{" "}
                      {t("kingCoconutWater.productDetails.imageCounter.of")}{" "}
                      {products[activeProduct].images.length}
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="p-8 md:p-12">
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                      {products[activeProduct].name}
                    </h3>

                    {/* Appearance & Taste */}
                    <div className="space-y-4 mb-6">
                      {getProductData(
                        products[activeProduct].key,
                        "appearance"
                      ) && (
                        <div>
                          <span className="font-bold text-orange-600">
                            {t("kingCoconutWater.productDetails.appearance")}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 ml-2">
                            {getProductData(
                              products[activeProduct].key,
                              "appearance"
                            )}
                          </span>
                        </div>
                      )}
                      {getProductData(products[activeProduct].key, "aroma") && (
                        <div>
                          <span className="font-bold text-orange-600">
                            {t("kingCoconutWater.productDetails.aroma")}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 ml-2">
                            {getProductData(
                              products[activeProduct].key,
                              "aroma"
                            )}
                          </span>
                        </div>
                      )}
                      {getProductData(
                        products[activeProduct].key,
                        "flavour"
                      ) && (
                        <div>
                          <span className="font-bold text-orange-600">
                            {t("kingCoconutWater.productDetails.flavour")}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 ml-2">
                            {getProductData(
                              products[activeProduct].key,
                              "flavour"
                            )}
                          </span>
                        </div>
                      )}
                      {getProductData(
                        products[activeProduct].key,
                        "tasteProfile"
                      ) && (
                        <div>
                          <span className="font-bold text-orange-600">
                            {t("kingCoconutWater.productDetails.tasteProfile")}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 ml-2">
                            {getProductData(
                              products[activeProduct].key,
                              "tasteProfile"
                            )}
                          </span>
                        </div>
                      )}
                      {getProductData(
                        products[activeProduct].key,
                        "texture"
                      ) && (
                        <div>
                          <span className="font-bold text-orange-600">
                            {t("kingCoconutWater.productDetails.texture")}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 ml-2">
                            {getProductData(
                              products[activeProduct].key,
                              "texture"
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Ingredients */}
                    <div className="bg-orange-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                      <h4 className="font-bold text-orange-600 dark:text-orange-400 mb-2">
                        {t("kingCoconutWater.productDetails.ingredients")}
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300">
                        {getProductData(
                          products[activeProduct].key,
                          "ingredients"
                        )}
                      </p>
                      {getProductData(
                        products[activeProduct].key,
                        "noAdded"
                      ) && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          <strong>
                            {t("kingCoconutWater.productDetails.noAdded")}
                          </strong>{" "}
                          {getProductData(
                            products[activeProduct].key,
                            "noAdded"
                          )}
                        </p>
                      )}
                    </div>

                    {/* Serving Suggestions */}
                    {getProductData(
                      products[activeProduct].key,
                      "servingSuggestions"
                    ) && (
                      <div className="mb-6">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                          {t(
                            "kingCoconutWater.productDetails.servingSuggestions"
                          )}
                        </h4>
                        <ul className="space-y-2">
                          {getProductData(
                            products[activeProduct].key,
                            "servingSuggestions"
                          ).map((suggestion, i) => (
                            <li key={i} className="flex items-start space-x-2">
                              <FaCheckCircle className="text-orange-600 mt-1 flex-shrink-0" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {suggestion}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Benefits */}
                    {getProductData(
                      products[activeProduct].key,
                      "benefits"
                    ) && (
                      <div className="mb-6">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                          {t("kingCoconutWater.productDetails.keyBenefits")}
                        </h4>
                        <ul className="space-y-2">
                          {getProductData(
                            products[activeProduct].key,
                            "benefits"
                          ).map((benefit, i) => (
                            <li key={i} className="flex items-start space-x-2">
                              <FaHeart className="text-orange-600 mt-1 flex-shrink-0" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {benefit}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Why Love */}
                    {getProductData(products[activeProduct].key, "whyLove") && (
                      <div className="bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl p-4">
                        <p className="text-gray-700 dark:text-gray-300">
                          <strong className="text-orange-600">
                            {t("kingCoconutWater.productDetails.whyLove")}
                          </strong>{" "}
                          {getProductData(
                            products[activeProduct].key,
                            "whyLove"
                          )}
                        </p>
                      </div>
                    )}

                    {/* Common Specs */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {t(
                              "kingCoconutWater.productDetails.countryOfOrigin"
                            )}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t("kingCoconutWater.productDetails.countryValue")}
                          </p>
                        </div>
                        <div>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {t("kingCoconutWater.productDetails.shelfLife")}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t(
                              "kingCoconutWater.productDetails.shelfLifeValue"
                            )}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <span className="font-bold text-gray-900 dark:text-white">
                            {t("kingCoconutWater.productDetails.packSizes")}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t(
                              "kingCoconutWater.productDetails.packSizesValue"
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Description Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl p-8 text-white shadow-xl"
            >
              <p className="text-lg leading-relaxed">
                {t("kingCoconutWater.productLine.description")}
              </p>
            </motion.div>
          </motion.div>
        </section>

        {/* Certifications */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("kingCoconutWater.certifications.title")}
            </h2>

            <div className="flex flex-wrap justify-center items-center gap-6 max-w-5xl mx-auto">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  className="group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    {/* Logo */}
                    <div className="w-16 h-16 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-xl p-2 group-hover:bg-orange-100 dark:group-hover:bg-gray-600 transition-colors">
                      <img
                        src={cert.image}
                        alt={cert.alt}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `<span class="text-2xl">${cert.icon}</span>`;
                        }}
                      />
                    </div>

                    {/* Name */}
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center max-w-[80px]">
                      {cert.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quality & Safety */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("kingCoconutWater.quality.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {["brc", "traceability", "monitoring"].map((key, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-amber-600 mb-4 flex justify-center">
                    {key === "brc" ? (
                      <FaShieldAlt />
                    ) : key === "traceability" ? (
                      <FaBarcode />
                    ) : (
                      <FaThermometerHalf />
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {t(`kingCoconutWater.quality.items.${key}.title`)}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t(`kingCoconutWater.quality.items.${key}.description`)}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Sustainability & Sourcing */}
        <section className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("kingCoconutWater.sustainability.title")}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {["farmers", "organic", "fairtrade"].map((key, i) => (
                  <div
                    key={i}
                    className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg"
                  >
                    <div className="text-5xl text-green-600 mb-4">
                      {key === "farmers" ? (
                        <FaHeart />
                      ) : key === "organic" ? (
                        <FaLeaf />
                      ) : (
                        <FaCertificate />
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {t(`kingCoconutWater.sustainability.items.${key}.title`)}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      {t(
                        `kingCoconutWater.sustainability.items.${key}.description`
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Usage & Storage */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              {t("kingCoconutWater.usage.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: <FaGlassWhiskey />, key: "chilled" },
                { icon: <FaTemperatureHigh />, key: "refrigerate" },
                { icon: <FaTint />, key: "freeze" },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-5xl text-orange-600 mb-4 flex justify-center">
                    {item.icon}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">
                    {t(`kingCoconutWater.usage.items.${item.key}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("kingCoconutWater.faq.title")}
              </h2>
              <div className="space-y-4">
                {faqKeys.map((key, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-amber-50 dark:bg-gray-700 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() =>
                        setOpenFaq(openFaq === index ? null : index)
                      }
                      className="w-full text-left p-6 flex justify-between items-center hover:bg-amber-100 dark:hover:bg-gray-600 transition-all duration-300"
                    >
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white pr-4">
                        {t(`kingCoconutWater.faq.items.${key}.question`)}
                      </h3>
                      <motion.div
                        animate={{ rotate: openFaq === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <FaCheckCircle className="text-amber-600 text-2xl flex-shrink-0" />
                      </motion.div>
                    </button>
                    <motion.div
                      initial={false}
                      animate={{ height: openFaq === index ? "auto" : 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-gray-700 dark:text-gray-300">
                        {t(`kingCoconutWater.faq.items.${key}.answer`)}
                      </p>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Technical Specs Teaser */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl"
          >
            <div className="text-center">
              <FaDownload className="text-6xl mb-4 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("kingCoconutWater.technical.title")}
              </h2>
              <p className="text-lg mb-6 opacity-90">
                {t("kingCoconutWater.technical.description")}
              </p>
              <button
                onClick={() => setShowDownloadModal(true)}
                className="bg-white text-indigo-600 px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 flex items-center space-x-2 mx-auto"
              >
                <FaDownload />
                <span>{t("kingCoconutWater.technical.downloadButton")}</span>
              </button>
            </div>
          </motion.div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default KingCoconutWater;
